import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  InsightsChartSkeleton,
  InsightsEmptyState,
} from './Insights/InsightsAsyncState';
import TableDisplay from './TableDisplay';
import ExportCSV from './ExportCSV';
import { statisticCardContainerSx } from '../routes/pricing-reports/constants';

const DataVisualiser = ({ 
    data = [], 
    width = '100%', 
    height = 500, 
    margin = { top: 20, right: 30, bottom: 40, left: 40 },
    xKey = 'province',
    yKey = 'count',
    xLabel = 'Category',
    yLabel = 'Value',
    title = 'Bar Chart',
    description = 'Data visualization',
    colorScheme = d3.schemeCategory10,
    sortBy = 'desc', // 'asc', 'desc', or 'none'
    showTooltip = true,
    showAxes = true,
    showLabels = true,
    showXAxisValues = true, // New prop to control x-axis values visibility
    showYAxisValues = false, // New prop to control y-axis values visibility
    barPadding = 0.1,
    emptyMessage = 'No data available',
    isLoading = false,
    horizontal = false // New prop for horizontal orientation
}) => {
    const svgRef = useRef();
    const containerRef = useRef();
    const retailer = useSelector(state => state.retailer);
    const [activeTab, setActiveTab] = useState(0);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    useEffect(() => {
        if (!data || data.length === 0) return;

        // Clear previous chart
        d3.select(svgRef.current).selectAll("*").remove();

        // Get container width for responsive sizing
        const getContainerWidth = () => {
            if (typeof width === 'number') return width;
            if (containerRef.current) {
                return containerRef.current.offsetWidth;
            }
            return 800; // fallback width
        };

        const currentWidth = getContainerWidth();
        const innerWidth = currentWidth - margin.left - margin.right;
        const innerHeight = height - margin.top - margin.bottom;

        // Create SVG
        const svg = d3.select(svgRef.current)
            .attr("width", currentWidth)
            .attr("height", height);

        const g = svg.append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Sort data based on sortBy prop
        let sortedData = [...data];
        if (sortBy === 'desc') {
            sortedData = sortedData.sort((a, b) => b[yKey] - a[yKey]);
        } else if (sortBy === 'asc') {
            sortedData = sortedData.sort((a, b) => a[yKey] - b[yKey]);
        }

        // Create scales - swap x and y for horizontal orientation
        const xScale = horizontal 
            ? d3.scaleLinear()
                .domain([0, d3.max(sortedData, d => d[yKey])])
                .range([0, innerWidth])
            : d3.scaleBand()
                .domain(sortedData.map(d => d[xKey] || 'Unknown'))
                .range([0, innerWidth])
                .padding(barPadding);

        const yScale = horizontal
            ? d3.scaleBand()
                .domain(sortedData.map(d => d[xKey] || 'Unknown'))
                .range([0, innerHeight])
                .padding(barPadding)
            : d3.scaleLinear()
                .domain([0, d3.max(sortedData, d => d[yKey])])
                .range([innerHeight, 0]);

        // Create color scale
        const colorScale = d3.scaleOrdinal()
            .domain(sortedData.map(d => d[xKey] || 'Unknown'))
            .range(colorScheme);

        // Add bars
        const bars = g.selectAll(".bar")
            .data(sortedData)
            .enter()
            .append("rect")
            .attr("class", "bar")
            .attr("x", d => horizontal ? 0 : xScale(d[xKey] || 'Unknown'))
            .attr("y", d => horizontal ? yScale(d[xKey] || 'Unknown') : yScale(d[yKey]))
            .attr("width", d => horizontal ? xScale(d[yKey]) : xScale.bandwidth())
            .attr("height", d => horizontal ? yScale.bandwidth() : innerHeight - yScale(d[yKey]))
            .attr("fill", d => colorScale(d[xKey] || 'Unknown'))
            .attr("stroke", "#fff")
            .attr("stroke-width", 1);

        // Add tooltip functionality if enabled
        if (showTooltip) {
            bars
                .on("mouseover", function(event, d) {
                    // Show tooltip
                    const tooltip = d3.select("body").append("div")
                        .attr("class", "tooltip")
                        .style("position", "absolute")
                        .style("background", "rgba(0, 0, 0, 0.8)")
                        .style("color", "white")
                        .style("padding", "8px")
                        .style("border-radius", "4px")
                        .style("font-size", "12px")
                        .style("pointer-events", "none")
                        .style("z-index", "1000");

                    tooltip.html(`
                        <strong>${d[xKey] || 'Unknown'}</strong><br/>
                        ${yLabel}: ${d[yKey].toLocaleString()}
                    `)
                    .style("left", (event.pageX + 10) + "px")
                    .style("top", (event.pageY - 10) + "px");

                    // Highlight bar
                    d3.select(this)
                        .attr("opacity", 0.8)
                        .attr("stroke", "#000")
                        .attr("stroke-width", 2);
                })
                .on("mouseout", function() {
                    // Remove tooltip
                    d3.selectAll(".tooltip").remove();

                    // Reset bar appearance
                    d3.select(this)
                        .attr("opacity", 1)
                        .attr("stroke", "#fff")
                        .attr("stroke-width", 1);
                });
        }

        // Add axes if enabled
        if (showAxes) {
            if (horizontal) {
                // Add X axis (bottom) for horizontal bars
                const xAxis = g.append("g")
                    .attr("transform", `translate(0,${innerHeight})`)
                    .call(d3.axisBottom(xScale).tickFormat(d3.format(".0s")));
                
                // Hide x-axis values if showXAxisValues is false
                if (!showXAxisValues) {
                    xAxis.selectAll("text").style("display", "none");
                }

                // Add Y axis (left) for horizontal bars
                const yAxis = g.append("g")
                    .call(d3.axisLeft(yScale))
                    .selectAll("text")
                    .style("text-anchor", "end")
                    .attr("dx", "-.8em")
                    .attr("dy", ".15em");
                
                // Hide y-axis values if showYAxisValues is false
                if (!showYAxisValues) {
                    yAxis.style("display", "none");
                }
            } else {
                // Add X axis (bottom) for vertical bars
                const xAxis = g.append("g")
                    .attr("transform", `translate(0,${innerHeight})`)
                    .call(d3.axisBottom(xScale))
                    .selectAll("text")
                    .style("text-anchor", "end")
                    .attr("dx", "-.8em")
                    .attr("dy", ".15em")
                    .attr("transform", "rotate(-45)");
                
                // Hide x-axis values if showXAxisValues is false
                if (!showXAxisValues) {
                    xAxis.style("display", "none");
                }

                // Add Y axis (left) for vertical bars
                const yAxis = g.append("g")
                    .call(d3.axisLeft(yScale).tickFormat(d3.format(".0s")));
                
                // Hide y-axis values if showYAxisValues is false
                if (!showYAxisValues) {
                    yAxis.selectAll("text").style("display", "none");
                }
            }
        }

        // Add axis labels if enabled
        if (showLabels) {
            if (horizontal) {
                // X axis label (bottom) for horizontal bars
                g.append("text")
                    .attr("transform", `translate(${innerWidth / 2}, ${innerHeight + margin.bottom - 10})`)
                    .style("text-anchor", "middle")
                    .style("font-size", "12px")
                    .text(yLabel);

                // Y axis label (left) for horizontal bars
                g.append("text")
                    .attr("transform", "rotate(-90)")
                    .attr("y", 0 - margin.left)
                    .attr("x", 0 - (innerHeight / 2))
                    .attr("dy", "1em")
                    .style("text-anchor", "middle")
                    .style("font-size", "12px")
                    .text(xLabel);
            } else {
                // Y axis label (left) for vertical bars
                g.append("text")
                    .attr("transform", "rotate(-90)")
                    .attr("y", 0 - margin.left)
                    .attr("x", 0 - (innerHeight / 2))
                    .attr("dy", "1em")
                    .style("text-anchor", "middle")
                    .style("font-size", "12px")
                    .text(yLabel);

                // X axis label (bottom) for vertical bars
                g.append("text")
                    .attr("transform", `translate(${innerWidth / 2}, ${innerHeight + margin.bottom - 10})`)
                    .style("text-anchor", "middle")
                    .style("font-size", "12px")
                    .text(xLabel);
            }
        }

    }, [data, width, height, margin, xKey, yKey, sortBy, showTooltip, showAxes, showLabels, showXAxisValues, showYAxisValues, barPadding, colorScheme, horizontal]);

    // Add resize listener for responsive behavior
    useEffect(() => {
        const handleResize = () => {
            if (typeof width === 'string' && width.includes('%')) {
                // Trigger re-render on window resize for percentage widths
                const currentWidth = containerRef.current?.offsetWidth;
                if (currentWidth) {
                    d3.select(svgRef.current)
                        .attr("width", currentWidth);
                }
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [width]);

    if (isLoading) {
        return (
            <Box sx={{ mt: 2, p: 2, ...statisticCardContainerSx }}>
                <InsightsChartSkeleton height={height} />
            </Box>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Box sx={{ mt: 2, p: 2, ...statisticCardContainerSx }}>
                <InsightsEmptyState message={emptyMessage} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                mt: 2,
                p: 2,
                ...statisticCardContainerSx,
            }}
        >
            {(title || description) && (
                <Box
                    sx={{ 
                        mb: 2, 
                        borderLeft: `4px solid ${retailer?.brand_color || 'rgb(51, 51, 51)'}`, 
                        pl: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                    }}
                >
                    <Box>
                        {title && (
                            <Typography gutterBottom
                                sx={{
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                }}
                            >
                                {title}
                            </Typography>
                        )}

                        {description && (
                            <Typography variant="body2" color="text.secondary">
                                {description}
                            </Typography>
                        )}
                    </Box>
                    
                    <ExportCSV
                        data={data}
                        fileName={`${title?.toLowerCase().replace(/\s+/g, '-') || 'data'}-export.csv`}
                        xKey={xKey}
                        yKey={yKey}
                        xLabel={xLabel}
                        yLabel={yLabel}
                        sortBy={sortBy}
                    />
                </Box>
            )}

            {/* <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="data visualization tabs">
                    <Tab label="Table View" />
                    <Tab label="Chart View" />
                </Tabs>
            </Box>

            {activeTab === 1 && (
                <BarChart
                    data={data}
                    width={width}
                    height={height}
                    margin={margin}
                    xKey={xKey}
                    yKey={yKey}
                    xLabel={xLabel}
                    yLabel={yLabel}
                    title=""
                    description=""
                    colorScheme={colorScheme}
                    sortBy={sortBy}
                    showTooltip={showTooltip}
                    showAxes={showAxes}
                    showLabels={showLabels}
                    showXAxisValues={showXAxisValues}
                    showYAxisValues={showYAxisValues}
                    barPadding={barPadding}
                    emptyMessage={emptyMessage}
                    horizontal={horizontal}
                />
            )} */}

            {activeTab === 0 && (
                <TableDisplay
                    data={data}
                    xKey={xKey}
                    yKey={yKey}
                    xLabel={xLabel}
                    yLabel={yLabel}
                    title=""
                    description=""
                    sortBy={sortBy}
                    emptyMessage={emptyMessage}
                />
            )}
        </Box>
    );
};

export default DataVisualiser;
